/**
 * API Service - Axios instance with interceptors
 * Handles authentication, error handling, and request/response logging
 */

import axios from "axios";
import environment from "../config/environment";

// Create axios instance
const api = axios.create({
  baseURL: environment.API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Auth token storage key
const TOKEN_KEY = "srop_auth_token";
const API_KEY_STORAGE = "srop_api_key";

// Get stored auth token
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const removeAuthToken = () => localStorage.removeItem(TOKEN_KEY);

// Get stored API key
export const getApiKey = () => localStorage.getItem(API_KEY_STORAGE);
export const setApiKey = (key) => localStorage.setItem(API_KEY_STORAGE, key);
export const removeApiKey = () => localStorage.removeItem(API_KEY_STORAGE);

// Request interceptor - Add auth headers
api.interceptors.request.use(
  (config) => {
    // Add JWT token if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add API key if available
    const apiKey = getApiKey();
    if (apiKey) {
      config.headers["X-API-Key"] = apiKey;
    }

    // Debug logging
    if (environment.ENABLE_DEBUG) {
      console.log(
        `[API] ${config.method?.toUpperCase()} ${config.url}`,
        config.data || "",
      );
    }

    return config;
  },
  (error) => {
    console.error("[API] Request error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    if (environment.ENABLE_DEBUG) {
      console.log(`[API] Response ${response.status}:`, response.data);
    }
    return response;
  },
  (error) => {
    const { response } = error;

    console.log("stopping to get redirected to login page");
    return;

    if (response) {
      // Handle specific error codes
      switch (response.status) {
        case 401:
          // Unauthorized - but don't logout if we're in demo mode
          const token = localStorage.getItem("srop_auth_token");
          const storedEmail = localStorage.getItem("srop_user_email");
          const isDemoMode = token?.startsWith("demo_") && storedEmail;

          if (isDemoMode) {
            // In demo mode, 401 from backend is expected - just log and continue
            console.warn(
              "[API] 401 from backend in demo mode - using mock data fallback",
            );
          } else {
            // Production mode - clear tokens and redirect to login
            removeAuthToken();
            removeApiKey();
            localStorage.removeItem("srop_user_email");
            // Don't redirect if already on login page
            if (!window.location.pathname.includes("/login")) {
              window.location.href = "/login";
            }
          }
          break;
        case 403:
          console.error("[API] Forbidden - insufficient permissions");
          break;
        case 404:
          // API endpoint not found - expected when backend not running
          console.warn("[API] 404 - endpoint not available, using fallback");
          break;
        case 429:
          console.error("[API] Rate limited - too many requests");
          break;
        case 500:
          console.error("[API] Server error:", response.data);
          break;
        default:
          console.error(`[API] Error ${response.status}:`, response.data);
      }
    } else if (error.request) {
      console.error("[API] No response received:", error.message);
    } else {
      console.error("[API] Request setup error:", error.message);
    }

    return Promise.reject(error);
  },
);

export default api;
